import React, { useEffect, useMemo, useState } from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";

import ridersLogo from "../images/ridersmiami-logo.png";

import {
  getOrdenesPedido,
  getOrdenPedidoById,
  anularOrdenPedido,
} from "../helpers/rutaOrdenPedido";

import "../css/InformeOrdenesPedido.css";

const fechaCorta = (fecha) => {
  if (!fecha) return "-";
  const d = new Date(fecha);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("es-AR");
};

const fechaHora = (fecha) => {
  if (!fecha) return "-";
  const d = new Date(fecha);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("es-AR");
};

const texto = (v, fallback = "-") => {
  const t = String(v ?? "").trim();
  return t || fallback;
};

const cargarImagenBase64 = (src) =>
  new Promise((resolve) => {
    const img = new Image();

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        resolve(canvas.toDataURL("image/png"));
      } catch (error) {
        console.error("Error convirtiendo logo a base64:", error);
        resolve(null);
      }
    };

    img.onerror = () => {
      console.error("No se pudo cargar el logo Riders Miami.");
      resolve(null);
    };

    img.src = src;
});

const generarPDFOrdenPedido = async (orden) => {
  const doc = new jsPDF("l", "mm", "a4");

  const nro = orden?.nroOrdenPedido || "-";
  const fecha = fechaCorta(orden?.fecha);

  // Encabezado tipo logo simple, luego lo reemplazamos por imagen real si querés
  const logoBase64 = await cargarImagenBase64(ridersLogo);

if (logoBase64) {
  doc.addImage(logoBase64, "PNG", 12, 7, 32, 14);
} else {
  doc.setFontSize(18);
  doc.setTextColor(220, 0, 0);
  doc.text("RIDERS", 14, 14);

  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text("MIAMI", 42, 14);
}

doc.setFontSize(16);
doc.setTextColor(0, 0, 0);
doc.text(`ORDEN DE PEDIDO Nº ${nro}`, 14, 34);

doc.setFontSize(10);
doc.text(`Fecha: ${fecha}`, 14, 43);
doc.text(`Estado: ${orden?.estado || "-"}`, 14, 49);

doc.text(
  `Pedidos: ${orden?.totales?.cantidadPedidos || orden?.items?.length || 0}`,
  80,
  43
);

doc.text(`Unidades: ${orden?.totales?.totalUnidades || 0}`, 80, 49);

if (orden?.observaciones) {
  doc.text(`Observación: ${orden.observaciones}`, 14, 58);
}

const filas = [];

(orden?.items || []).forEach((item, index) => {
  filas.push([
    index + 1,
    fechaCorta(item.fechaPedidoCliente),
    item.track || "-",
    item.articulo || "-",
    item.codprod || "-",
    item.cantidadOrdenada || item.cantidadOriginal || 1,
    item.clienteUSA || "-",
    item.nombreReceptor || "-",
    item.carnetIdentidad || "-",
    item.telefonoPrincipal || "-",
    item.provincia || "-",
    item.municipio || "-",
    item.zonaLogistica || "-",
  ]);

  filas.push([
    {
      content: `DOMICILIO: ${item.direccionCompleta || item.direccion || "-"}`,
      colSpan: 13,
      styles: {
        fontStyle: "bold",
        textColor: [30, 30, 30],
        fillColor: [245, 245, 245],
        cellPadding: 2,
      },
    },
  ]);
});

doc.autoTable({
  startY: orden?.observaciones ? 64 : 58,
  head: [
    [
      "#",
      "Fecha",
      "Track",
      "Producto",
      "Cod.",
      "Cant.",
      "Cliente USA",
      "Receptor",
      "ID",
      "Teléfono",
      "Provincia",
      "Municipio",
      "Zona",
    ],
  ],
  body: filas,
  styles: {
    fontSize: 7,
    cellPadding: 1.5,
    overflow: "linebreak",
    valign: "top",
  },
  headStyles: {
    fillColor: [220, 0, 0],
    textColor: [255, 255, 255],
    fontStyle: "bold",
  },
  alternateRowStyles: {
    fillColor: [255, 255, 255],
  },
  columnStyles: {
    0: { cellWidth: 7 },
    1: { cellWidth: 16 },
    2: { cellWidth: 20 },
    3: { cellWidth: 28 },
    4: { cellWidth: 10 },
    5: { cellWidth: 9 },
    6: { cellWidth: 30 }, // Cliente USA
    7: { cellWidth: 30 }, // Receptor
    8: { cellWidth: 22 }, // ID
    9: { cellWidth: 20 }, // Teléfono
    10: { cellWidth: 20 }, // Provincia
    11: { cellWidth: 22 }, // Municipio
    12: { cellWidth: 28 }, // Zona
  },
  margin: { left: 8, right: 8 },
});

  const pageCount = doc.internal.getNumberOfPages();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(
      `Orden de Pedido Nº ${nro} - Página ${i} de ${pageCount}`,
      14,
      200
    );
  }

  doc.save(`OrdenPedido_${nro}.pdf`);
};

const generarCSVOrdenPedido = (orden) => {
  const nro = orden?.nroOrdenPedido || "-";

  const escaparCSV = (valor) =>
    `"${String(valor ?? "").replace(/"/g, '""')}"`;

  const headers = [
    "#",
    "Fecha",
    "Track",
    "Producto",
    "Código",
    "Cantidad",
    "Cliente USA",
    "Receptor",
    "ID",
    "Teléfono",
    "Provincia",
    "Municipio",
    "Zona",
    "Domicilio",
  ];

  const filas = (orden?.items || []).map((item, index) => [
    index + 1,
    fechaCorta(item.fechaPedidoCliente),
    item.track || "",
    item.articulo || "",
    item.codprod || "",
    item.cantidadOrdenada || item.cantidadOriginal || 1,
    item.clienteUSA || "",
    item.nombreReceptor || "",
    item.carnetIdentidad || "",
    item.telefonoPrincipal || "",
    item.provincia || "",
    item.municipio || "",
    item.zonaLogistica || "",
    item.direccionCompleta || item.direccion || "",
  ]);

  const csv = [
    [`ORDEN DE PEDIDO Nº ${nro}`].map(escaparCSV).join(";"),

    [
      "Fecha",
      fechaCorta(orden?.fecha),
      "Estado",
      orden?.estado || "",
    ]
      .map(escaparCSV)
      .join(";"),

    [
      "Pedidos",
      orden?.totales?.cantidadPedidos || orden?.items?.length || 0,
      "Unidades",
      orden?.totales?.totalUnidades || 0,
    ]
      .map(escaparCSV)
      .join(";"),

    [
      "Observación",
      orden?.observaciones || "",
    ]
      .map(escaparCSV)
      .join(";"),

    "",

    headers.map(escaparCSV).join(";"),

    ...filas.map((fila) =>
      fila.map(escaparCSV).join(";")
    ),
  ].join("\n");

  const blob = new Blob(["\uFEFF" + csv], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");

  a.href = url;
  a.download = `OrdenPedido_${nro}.csv`;

  document.body.appendChild(a);

  a.click();

  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export default function InformeOrdenesPedido() {
  const [ordenes, setOrdenes] = useState([]);
  const [buscar, setBuscar] = useState("");
  const [estado, setEstado] = useState("");
  const [loading, setLoading] = useState(false);

  const cargarOrdenes = async () => {
    try {
      setLoading(true);

      const data = await getOrdenesPedido({
        buscar,
        estado,
      });

      setOrdenes(Array.isArray(data?.ordenes) ? data.ordenes : []);
    } catch (err) {
      console.error("Error cargando órdenes de pedido:", err);
      alert(
        err?.response?.data?.mensaje ||
          err?.response?.data?.error ||
          err?.message ||
          "Error al cargar órdenes de pedido"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      cargarOrdenes();
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscar, estado]);

  const totales = useMemo(() => {
    return ordenes.reduce(
      (acc, orden) => {
        acc.ordenes += 1;
        acc.pedidos += Number(orden?.totales?.cantidadPedidos || 0);
        acc.unidades += Number(orden?.totales?.totalUnidades || 0);

        if (orden?.estado === "GENERADA") acc.generadas += 1;
        if (orden?.estado === "ANULADA") acc.anuladas += 1;

        return acc;
      },
      {
        ordenes: 0,
        pedidos: 0,
        unidades: 0,
        generadas: 0,
        anuladas: 0,
      }
    );
  }, [ordenes]);

  const imprimirPDF = async (id) => {
    try {
      const orden = await getOrdenPedidoById(id);
      await generarPDFOrdenPedido(orden);
    } catch (err) {
      console.error("Error generando PDF:", err);
      alert(
        err?.response?.data?.mensaje ||
          err?.response?.data?.error ||
          err?.message ||
          "Error al generar PDF"
      );
    }
  };

  const imprimirCSV = async (id) => {
    try {
      const orden = await getOrdenPedidoById(id);

      generarCSVOrdenPedido(orden);
    } catch (err) {
      console.error("Error generando CSV:", err);

      alert(
        err?.response?.data?.mensaje ||
          err?.response?.data?.error ||
          err?.message ||
          "Error al generar CSV"
      );
    }
  };

  const anular = async (orden) => {
    if (!orden?._id) return;

    const ok = window.confirm(
      `¿Anular la Orden de Pedido Nº ${orden.nroOrdenPedido}?\n\nLos pedidos volverán a estar disponibles para organizar.`
    );

    if (!ok) return;

    try {
      const motivo =
        window.prompt("Motivo de anulación:", "ANULADA DESDE INFORME") ||
        "ANULADA DESDE INFORME";

      await anularOrdenPedido(orden._id, motivo);

      alert("Orden anulada correctamente. Los pedidos volvieron a disponibles.");

      await cargarOrdenes();
    } catch (err) {
      console.error("Error anulando orden:", err);
      alert(
        err?.response?.data?.mensaje ||
          err?.response?.data?.error ||
          err?.message ||
          "Error al anular orden"
      );
    }
  };

  return (
    <div className="iop-wrap">
      <div className="iop-header">
        <div>
          <h1>Órdenes de Pedido</h1>
          <p>Listado de órdenes generadas para proveedores y logística.</p>
        </div>

        <button type="button" className="iop-btn" onClick={cargarOrdenes}>
          Actualizar
        </button>
      </div>

      <div className="iop-resumen">
        <div>
          <strong>{totales.ordenes}</strong>
          <span>Órdenes</span>
        </div>
        <div>
          <strong>{totales.generadas}</strong>
          <span>Generadas</span>
        </div>
        <div>
          <strong>{totales.anuladas}</strong>
          <span>Anuladas</span>
        </div>
        <div>
          <strong>{totales.pedidos}</strong>
          <span>Pedidos</span>
        </div>
        <div>
          <strong>{totales.unidades}</strong>
          <span>Unidades</span>
        </div>
      </div>

      <div className="iop-card">
        <div className="iop-filtros">
          <input
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            placeholder="Buscar por número, track, receptor, producto..."
          />

          <select value={estado} onChange={(e) => setEstado(e.target.value)}>
            <option value="">Todos los estados</option>
            <option value="GENERADA">Generada</option>
            <option value="ANULADA">Anulada</option>
          </select>
        </div>

        <div className="iop-table-wrap">
          <table className="iop-table">
            <thead>
              <tr>
                <th>Nº Orden</th>
                <th>Fecha</th>
                <th>Estado</th>
                <th>Pedidos</th>
                <th>Unidades</th>
                <th>Observación</th>
                <th>Actualizada</th>
                <th>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {ordenes.map((orden) => (
                <tr key={orden._id}>
                  <td>
                    <strong>{orden.nroOrdenPedido}</strong>
                  </td>
                  <td>{fechaCorta(orden.fecha)}</td>
                  <td>
                    <span
                      className={`iop-pill ${
                        orden.estado === "ANULADA" ? "danger" : "success"
                      }`}
                    >
                      {orden.estado}
                    </span>
                  </td>
                  <td>{orden?.totales?.cantidadPedidos || 0}</td>
                  <td>{orden?.totales?.totalUnidades || 0}</td>
                  <td>{texto(orden.observaciones)}</td>
                  <td>{fechaHora(orden.updatedAt)}</td>
                  <td>
                    <div className="iop-actions">
                      <button
                        type="button"
                        className="iop-action pdf"
                        onClick={() => imprimirPDF(orden._id)}
                      >
                        PDF
                      </button>

                      <button
                        type="button"
                        className="iop-action pdf"
                        onClick={() => imprimirCSV(orden._id)}
                      >
                        CSV
                      </button>

                      {orden.estado !== "ANULADA" && (
                        <button
                          type="button"
                          className="iop-action danger"
                          onClick={() => anular(orden)}
                        >
                          Anular
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}

              {!loading && ordenes.length === 0 && (
                <tr>
                  <td colSpan="8" className="iop-empty">
                    No hay órdenes de pedido.
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td colSpan="8" className="iop-empty">
                    Cargando órdenes...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}